import React, { useEffect, useState } from 'react'
import { customSwal } from '../../../../config/swal'
import { InfoTooltip } from '../../../../common/components/InfoTooltip'
import { BackofficePermissionsEnum as Permissions } from '../../../../common/enums/backOfficePermissionsEnum'
import usePermissions from '../../../../common/hooks/usePermissions'
import { FundDraftService, IFundDraftData } from '../../../../services/FundDraftService'
import { TabSkeleton } from '../../../../common/components/TabSkeleton'
import { useFundSeries } from '../hooks/useFundSeries'
import { ImageDropZone } from './ImageDropZone'

const imagenProductoTooltip = (
  <div>
    <div style={{ fontWeight: 700, marginBottom: 8 }}>Medidas</div>
    <div style={{ fontWeight: 600, marginBottom: 2 }}>Web</div>
    <div style={{ fontSize: 13 }}>Card 324 px x 63 px</div>
    <div style={{ fontSize: 13, marginBottom: 8 }}>Header 622 px x 70 px</div>
    <div style={{ fontWeight: 600, marginBottom: 2 }}>App</div>
    <div style={{ fontSize: 13 }}>Card 324 px x 63 px</div>
    <div style={{ fontSize: 13 }}>Header 361 px x 116 px</div>
  </div>
)

const publicidadTooltip = (
  <div>
    <div style={{ fontWeight: 700, marginBottom: 8 }}>Medidas</div>
    <div style={{ fontWeight: 600, marginBottom: 2 }}>Formato vertical</div>
    <div style={{ fontSize: 13, marginBottom: 8 }}>325 px x 586 px</div>
    <div style={{ fontWeight: 600, marginBottom: 2 }}>Formato horizontal</div>
    <div style={{ fontSize: 13 }}>542 px x 210 px</div>
  </div>
)

interface IProps {
  fundId: string
  onDirtyChange?: (dirty: boolean) => void
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

export const InfoRiesgoTab: React.FC<IProps> = ({ fundId, onDirtyChange, saveRef }) => {
  const { hasPermission } = usePermissions()
  const { draft, isLoading, refetchDraft } = useFundSeries(fundId)
  const [draftData, setDraftData] = useState<IFundDraftData | null>(null)
  const [description, setDescription] = useState('')
  const [initialDescription, setInitialDescription] = useState('')
  const [dirtySlots, setDirtySlots] = useState({
    card_web: null as File | null,
    header_web: null as File | null,
    card_mobile: null as File | null,
    header_mobile: null as File | null,
    vertical_ad: null as File | null,
    horizontal_ad: null as File | null,
  })
  const [slotErrors, setSlotErrors] = useState<Record<string, string>>({})
  const [draftAction, setDraftAction] = useState<'saving' | 'publishing' | null>(null)
  const [resetKey, setResetKey] = useState(0)

  useEffect(() => {
    if (draft) {
      setDraftData(draft.draft)
      const desc = draft.draft.description ?? ''
      setDescription(desc)
      setInitialDescription(desc)
    }
  }, [draft])

  const isMetaDirty = description !== initialDescription
  const isDirty = isMetaDirty || Object.values(dirtySlots).some(Boolean)

  const buildDraftTasks = () => {
    const draftSvc = FundDraftService()
    const tasks: { key: string; upload: () => Promise<{ url: string }> }[] = []
    if (dirtySlots.card_web)
      tasks.push({
        key: 'card_web',
        upload: () => draftSvc.uploadImage(fundId, dirtySlots.card_web!, 'card', 'web'),
      })
    if (dirtySlots.header_web)
      tasks.push({
        key: 'header_web',
        upload: () => draftSvc.uploadImage(fundId, dirtySlots.header_web!, 'header', 'web'),
      })
    if (dirtySlots.card_mobile)
      tasks.push({
        key: 'card_mobile',
        upload: () => draftSvc.uploadImage(fundId, dirtySlots.card_mobile!, 'card', 'mobile'),
      })
    if (dirtySlots.header_mobile)
      tasks.push({
        key: 'header_mobile',
        upload: () => draftSvc.uploadImage(fundId, dirtySlots.header_mobile!, 'header', 'mobile'),
      })
    if (dirtySlots.vertical_ad)
      tasks.push({
        key: 'vertical_ad',
        upload: () => draftSvc.uploadAd(fundId, dirtySlots.vertical_ad!, 'vertical'),
      })
    if (dirtySlots.horizontal_ad)
      tasks.push({
        key: 'horizontal_ad',
        upload: () => draftSvc.uploadAd(fundId, dirtySlots.horizontal_ad!, 'horizontal'),
      })
    return { tasks, draftSvc }
  }

  const applyUploadResults = (uploadedUrls: Record<string, string>) => {
    setDraftData((prev) =>
      prev
        ? {
            ...prev,
            card_url_web: uploadedUrls.card_web ?? prev.card_url_web,
            card_url_mobile: uploadedUrls.card_mobile ?? prev.card_url_mobile,
            header_url_web: uploadedUrls.header_web ?? prev.header_url_web,
            header_url_mobile: uploadedUrls.header_mobile ?? prev.header_url_mobile,
            vertical_ad_url: uploadedUrls.vertical_ad ?? prev.vertical_ad_url,
            horizontal_ad_url: uploadedUrls.horizontal_ad ?? prev.horizontal_ad_url,
          }
        : null
    )
    setDirtySlots({
      card_web: null,
      header_web: null,
      card_mobile: null,
      header_mobile: null,
      vertical_ad: null,
      horizontal_ad: null,
    })
    setResetKey((k) => k + 1)
  }

  const runUploadsAndMeta = async (
    tasks: { key: string; upload: () => Promise<{ url: string }> }[],
    draftSvc: ReturnType<typeof FundDraftService>
  ) => {
    const [imageResults, metaResult] = await Promise.all([
      Promise.allSettled(tasks.map((t) => t.upload())),
      isMetaDirty
        ? draftSvc
            .updateDraft(fundId, { description })
            .then(() => ({ ok: true }))
            .catch(() => ({ ok: false }))
        : Promise.resolve({ ok: true }),
    ])

    const newErrors: Record<string, string> = {}
    const uploadedUrls: Record<string, string> = {}
    let hasErrors = false

    imageResults.forEach((result, i) => {
      if (result.status === 'fulfilled') uploadedUrls[tasks[i].key] = result.value.url
      else {
        hasErrors = true
        newErrors[tasks[i].key] = 'Error al subir la imagen'
      }
    })

    if (!(metaResult as { ok: boolean }).ok) {
      hasErrors = true
      newErrors['meta'] = 'Error al guardar la descripción'
    }

    return { hasErrors, newErrors, uploadedUrls }
  }

  const handleSave = async () => {
    const { tasks, draftSvc } = buildDraftTasks()
    if (tasks.length === 0 && !isMetaDirty) {
      customSwal.fire({ title: 'No hay cambios para guardar', icon: 'info' })
      return
    }
    setDraftAction('saving')
    setSlotErrors({})

    const { hasErrors, newErrors, uploadedUrls } = await runUploadsAndMeta(tasks, draftSvc)

    if (hasErrors) {
      setSlotErrors(newErrors)
      setDraftAction(null)
      return
    }

    applyUploadResults(uploadedUrls)
    setInitialDescription(description)
    refetchDraft()
    setDraftAction(null)
    customSwal.fire({ title: 'Cambios guardados correctamente', icon: 'success' })
  }

  const handlePublish = async () => {
    setDraftAction('publishing')
    setSlotErrors({})
    const { tasks, draftSvc } = buildDraftTasks()

    const { hasErrors, newErrors, uploadedUrls } = await runUploadsAndMeta(tasks, draftSvc)

    if (hasErrors) {
      setSlotErrors(newErrors)
      setDraftAction(null)
      return
    }

    if (tasks.length > 0) applyUploadResults(uploadedUrls)
    setInitialDescription(description)

    try {
      await draftSvc.publishDraft(fundId)
      refetchDraft()
      customSwal.fire({ title: 'Se ha publicado correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al publicar', icon: 'error' })
    }
    setDraftAction(null)
  }

  // Report dirty state to ConfigFunds
  useEffect(() => {
    onDirtyChange?.(isDirty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty])

  // Keep saveRef current
  useEffect(() => {
    if (saveRef) saveRef.current = handleSave
  })

  if (isLoading && !draft) return <TabSkeleton />

  return (
    <div>
      <div className='card mt-10 p-5'>
        <div className='d-flex align-items-center gap-3 mt-2 mb-1'>
          <span className='text-gray-800 fs-2 fw-bold'>Información del fondo (general)</span>
          {draft && (
            <span
              style={{
                background: '#d4edda',
                color: '#155724',
                fontSize: '12px',
                padding: '2px 10px',
                borderRadius: 20,
                fontWeight: 600,
              }}
            >
              Edición activo
            </span>
          )}
        </div>
        <span className='text-muted fs-6'>
          Datos generales que aplican a todas las series del fondo.
        </span>
      </div>

      {/* DESCRIPCIÓN + RAZÓN SOCIAL */}
      {hasPermission(Permissions.FUND_TABS.INFO.SHOW) && (
        <div className='card mt-10 p-10'>
          <div className='row g-8 mb-8'>
            <div className='col-lg-6'>
              <label className='fw-bolder fs-5 text-gray-700 mb-3'>Descripción</label>
              <textarea
                className={`form-control${slotErrors.meta ? ' is-invalid' : ''}`}
                name='description'
                style={{ height: '130px', resize: 'none' }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder='Descripción'
              />
              {slotErrors.meta && <div className='invalid-feedback'>{slotErrors.meta}</div>}
            </div>
            <div className='col-lg-6'>
              <label className='fw-bolder fs-5 text-gray-700 mb-3'>Razón social</label>
              <input
                type='text'
                disabled
                value={draft?.fund_title ?? ''}
                className='form-control'
                placeholder='Razón social'
              />
            </div>
          </div>

          {/* RESCATE PROGRAMADO (read-only) */}
          <div className='row'>
            <div className='col-lg-5'>
              <label className='fw-bolder fs-5 text-gray-700 mb-1'>Rescate programado</label>
              <span className='text-muted fs-7 mb-4 d-block'>
                Rescate programado solo para "text"
              </span>
              <div
                style={{
                  display: 'inline-flex',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  overflow: 'hidden',
                  opacity: 0.6,
                }}
              >
                <button
                  type='button'
                  disabled
                  style={{
                    padding: '6px 20px',
                    border: 'none',
                    background: draft?.has_scheduled_rescue ? 'var(--color-primary-blue)' : '#fff',
                    color: draft?.has_scheduled_rescue ? '#fff' : '#374151',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'not-allowed',
                  }}
                >
                  SÍ
                </button>
                <button
                  type='button'
                  disabled
                  style={{
                    padding: '6px 20px',
                    border: 'none',
                    borderLeft: '1px solid #d1d5db',
                    background: !draft?.has_scheduled_rescue ? 'var(--color-primary-blue)' : '#fff',
                    color: !draft?.has_scheduled_rescue ? '#fff' : '#374151',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'not-allowed',
                  }}
                >
                  NO
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CUENTAS RECAUDADORAS */}
      {draft?.collector_accounts && draft.collector_accounts.length > 0 && (
        <div className='card mt-10 p-10'>
          <span className='text-gray-800 fs-4 fw-bold mb-6 d-block'>Cuentas recaudadoras</span>
          <div style={{ overflowX: 'auto' }}>
            <table className='w-100' style={{ borderCollapse: 'separate', borderSpacing: '4px 4px' }}>
              <thead>
                <tr>
                  {['Banco', 'CCA', 'CCI', 'Código Spectrum', 'Moneda'].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        background: '#fafafa',
                        padding: '6px 12px',
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#374151',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {draft.collector_accounts.map((acc) => (
                  <tr key={acc.id}>
                    {[acc.bank_name, acc.cca, acc.cci, acc.id_spectrum_bank.trim(), acc.moneda].map(
                      (val, i) => (
                        <td
                          key={i}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            background: '#fff',
                            padding: '6px 12px',
                            fontSize: 13,
                            color: '#374151',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {val}
                        </td>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* IMAGEN DE PRODUCTO */}
      <div className='card mt-10 p-10'>
        <div className='d-flex align-items-center gap-2 mb-6'>
          <span className='text-gray-800 fs-4 fw-bold'>Imagen de producto</span>
          <InfoTooltip content={imagenProductoTooltip} />
        </div>
        <div className='row g-6'>
          <div className='col-lg-6'>
            <span className='fw-semibold text-gray-700 fs-6 mb-3 d-block'>Versión Web</span>
            <div className='d-flex gap-3 flex-wrap'>
              <ImageDropZone
                key={`card_web-${resetKey}`}
                label='Card'
                initialUrl={draftData?.card_url_web ?? undefined}
                onChange={(file) => setDirtySlots((prev) => ({ ...prev, card_web: file }))}
                error={slotErrors.card_web}
                disabled={!hasPermission(Permissions.FUND_TABS.INFO.IMAGE_UPLOAD)}
              />
              <ImageDropZone
                key={`header_web-${resetKey}`}
                label='Header'
                initialUrl={draftData?.header_url_web ?? undefined}
                onChange={(file) => setDirtySlots((prev) => ({ ...prev, header_web: file }))}
                error={slotErrors.header_web}
                disabled={!hasPermission(Permissions.FUND_TABS.INFO.IMAGE_UPLOAD)}
              />
            </div>
          </div>
          <div className='col-lg-6'>
            <span className='fw-semibold text-gray-700 fs-6 mb-3 d-block'>Versión App</span>
            <div className='d-flex gap-3 flex-wrap'>
              <ImageDropZone
                key={`card_mobile-${resetKey}`}
                label='Card'
                initialUrl={draftData?.card_url_mobile ?? undefined}
                onChange={(file) => setDirtySlots((prev) => ({ ...prev, card_mobile: file }))}
                error={slotErrors.card_mobile}
                disabled={!hasPermission(Permissions.FUND_TABS.INFO.IMAGE_UPLOAD)}
              />
              <ImageDropZone
                key={`header_mobile-${resetKey}`}
                label='Header'
                initialUrl={draftData?.header_url_mobile ?? undefined}
                onChange={(file) => setDirtySlots((prev) => ({ ...prev, header_mobile: file }))}
                error={slotErrors.header_mobile}
                disabled={!hasPermission(Permissions.FUND_TABS.INFO.IMAGE_UPLOAD)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* PUBLICIDAD */}
      <div className='card mt-10 p-10'>
        <div className='d-flex align-items-center gap-2 mb-6'>
          <span className='text-gray-800 fs-4 fw-bold'>Publicidad</span>
          <InfoTooltip content={publicidadTooltip} />
        </div>
        <div className='d-flex gap-5 flex-wrap'>
          <div>
            <span className='fw-semibold text-gray-700 fs-6 mb-3 d-block'>Formato vertical</span>
            <ImageDropZone
              key={`vertical_ad-${resetKey}`}
              initialUrl={draftData?.vertical_ad_url ?? undefined}
              onChange={(file) => setDirtySlots((prev) => ({ ...prev, vertical_ad: file }))}
              error={slotErrors.vertical_ad}
              disabled={!hasPermission(Permissions.FUND_TABS.INFO.IMAGE_UPLOAD)}
            />
          </div>
          <div>
            <span className='fw-semibold text-gray-700 fs-6 mb-3 d-block'>Formato horizontal</span>
            <ImageDropZone
              key={`horizontal_ad-${resetKey}`}
              initialUrl={draftData?.horizontal_ad_url ?? undefined}
              onChange={(file) => setDirtySlots((prev) => ({ ...prev, horizontal_ad: file }))}
              error={slotErrors.horizontal_ad}
              disabled={!hasPermission(Permissions.FUND_TABS.INFO.IMAGE_UPLOAD)}
            />
          </div>
        </div>
      </div>

      {/* ACCIONES DEL TAB */}
      {hasPermission([Permissions.FUND_TABS.INFO.SAVE, Permissions.FUND_TABS.INFO.PUBLISH]) && (
        <div className='card mt-5 p-5'>
          <div className='d-flex justify-content-end gap-3'>
            {hasPermission(Permissions.FUND_TABS.INFO.SAVE) && (
              <button
                type='button'
                className='btn btn-custom-action'
                onClick={handleSave}
                disabled={draftAction !== null}
              >
                {draftAction === 'saving' ? (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Guardando...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                ) : (
                  'Guardar cambios'
                )}
              </button>
            )}
            {hasPermission(Permissions.FUND_TABS.INFO.PUBLISH) && (
              <button
                type='button'
                className='btn btn-custom-action'
                onClick={handlePublish}
                disabled={draftAction !== null}
              >
                {draftAction === 'publishing' ? (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Publicando...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                ) : (
                  'Publicar'
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
